import * as i0 from '@angular/core';
import { InjectionToken, inject, TemplateRef, ViewContainerRef, Injector, input, numberAttribute, booleanAttribute, Directive } from '@angular/core';
import { TemplatePortal, DomPortalOutlet } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
const defaultToastConfig = {
  gap: 16,
  duration: 3000,
  gravity: 'top',
  position: 'end',
  stopOnHover: true,
  ariaLive: 'polite'
};
const NgpToastConfigToken = new InjectionToken('NgpToastConfigToken');
/**
 * Provide the default Toast configuration
 * @param config The Toast configuration
 * @returns The provider
 */
function provideToastConfig(config) {
  return [{
    provide: NgpToastConfigToken,
    useValue: {
      ...defaultToastConfig,
      ...config
    }
  }];
}
/**
 * Inject the Toast configuration
 * @returns The global Toast configuration
 */
function injectToastConfig() {
  return inject(NgpToastConfigToken, {
    optional: true
  }) ?? defaultToastConfig;
}
class NgpToastRef {
  /** Get the toast height */
  get height() {
    return this.toastElement.offsetHeight;
  }
  constructor(/** Store the toast element */
  toastElement, /** Store the duration */
  duration, /** The position of the toast */
  position, /** The gravity of the toast */
  gravity, /** Whether we should stop on focus */
  stopOnHover, /** The aria live setting */
  ariaLive, onDismiss) {
    this.toastElement = toastElement;
    this.duration = duration;
    this.position = position;
    this.gravity = gravity;
    this.stopOnHover = stopOnHover;
    this.ariaLive = ariaLive;
    this.onDismiss = onDismiss;
    /** Store the current timeout */
    this.timeoutId = null;
    this.toastElement.setAttribute('data-toast', 'visible');
    this.setPosition(position);
    this.setGravity(gravity);
    this.setAriaLive(ariaLive);
    this.setupTimeouts();
    this.setupListeners();
  }
  dismiss() {
    // determine if there is a transition on the element
    const transitionDuration = parseFloat(getComputedStyle(this.toastElement).transitionDuration);
    // if there is no transition, dismiss immediately
    if (transitionDuration === 0) {
      this.removeElement();
      return;
    }
    // wait for the transition to end
    this.toastElement.addEventListener('transitionend', () => this.removeElement());
    this.toastElement.setAttribute('data-toast', 'hidden');
  }
  removeElement() {
    this.toastElement.parentNode?.removeChild(this.toastElement);
    this.onDismiss();
  }
  /** Setup duration timeouts */
  setupTimeouts() {
    // if the duration is 0 skip
    if (this.duration === 0) {
      return;
    }
    this.timeoutId = window.setTimeout(() => this.dismiss(), this.duration);
  }
  setupListeners() {
    if (!this.stopOnHover) {
      return;
    }
    // setup event listeners if we should stop on focus
    this.toastElement.addEventListener('mouseover', () => {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    });
    this.toastElement.addEventListener('mouseleave', () => this.setupTimeouts());
  }
  /** Set the position attribute */
  setPosition(position) {
    this.toastElement.setAttribute('data-position', position);
  }
  /** Set the gravity attribute */
  setGravity(gravity) {
    this.toastElement.setAttribute('data-gravity', gravity);
  }
  /** Set the aria live attribute */
  setAriaLive(ariaLive) {
    this.toastElement.setAttribute('aria-live', ariaLive);
  }
  /**
   * @internal
   */
  setInset(property, value) {
    this.toastElement.style[property] = value;
  }
}
let NgpToast = /*#__PURE__*/(() => {
  class NgpToast {
    constructor() {
      this.config = injectToastConfig();
      /** Access the ng-template */
      this.template = inject(TemplateRef);
      /** Access the view container */
      this.viewContainer = inject(ViewContainerRef);
      /** Access the injector */
      this.injector = inject(Injector);
      /** Access the document */
      this.document = inject(DOCUMENT);
      /**
       * The duration the toast will display for before it is automatically dismissed in milliseconds.
       * @default 3000
       */
      this.duration = input(this.config.duration, {
        alias: 'ngpToastDuration',
        transform: numberAttribute
      });
      /**
       * The direction the toast will appear from.
       * @default 'top'
       */
      this.gravity = input(this.config.gravity, {
        alias: 'ngpToastGravity'
      });
      /**
       * The position the toast will on the horizontal axis.
       * @default 'end'
       */
      this.position = input(this.config.position, {
        alias: 'ngpToastPosition'
      });
      /**
       * Whether the automatic dismissal of the toast should be paused when the user hovers over it.
       * @default true
       */
      this.stopOnHover = input(this.config.stopOnHover, {
        alias: 'ngpToastStopOnHover',
        transform: booleanAttribute
      });
      /**
       * Whether the toast should be announced to assistive technologies.
       * @default 'polite'
       */
      this.ariaLive = input(this.config.ariaLive, {
        alias: 'ngpToastAriaLive'
      });
    }
    /** Store the list of toasts */
    static {
      this.toasts = [];
    }
    /** Show the toast. */
    show() {
      this.createToast();
      this.reposition();
    }
    /** Build the toast */
    createToast() {
      const portal = new TemplatePortal(this.template, this.viewContainer, {
        dismiss: () => toastRef.dismiss()
      }, this.injector);
      const domOutlet = new DomPortalOutlet(this.document.body, undefined, undefined, Injector.create({
        parent: this.injector,
        providers: []
      }));
      const viewRef = domOutlet.attach(portal);
      viewRef.detectChanges();
      const toastElement = viewRef.rootNodes[0];
      const toastRef = new NgpToastRef(toastElement, this.duration(), this.position(), this.gravity(), this.stopOnHover(), this.ariaLive(), () => {
        NgpToast.toasts = NgpToast.toasts.filter(t => t !== toastRef);
        this.reposition();
      });
      NgpToast.toasts = [...NgpToast.toasts, toastRef];
    }
    /** Position the toast on the DOM */
    reposition() {
      const topStartOffsetSize = {
        top: this.config.gap,
        bottom: this.config.gap
      };
      const topEndOffsetSize = {
        top: this.config.gap,
        bottom: this.config.gap
      };
      let position;
      // update the position of the toasts
      for (const toast of NgpToast.toasts) {
        // Getting the applied gravity
        position = toast.gravity;
        const height = toast.height;
        if (toast.position === 'start') {
          toast.setInset(position, `${topStartOffsetSize[position]}px`);
          topStartOffsetSize[position] += height + this.config.gap;
        } else {
          toast.setInset(position, `${topEndOffsetSize[position]}px`);
          topEndOffsetSize[position] += height + this.config.gap;
        }
      }
    }
    static {
      this.ɵfac = function NgpToast_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgpToast)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgpToast,
        selectors: [["", "ngpToast", ""]],
        inputs: {
          duration: [1, "ngpToastDuration", "duration"],
          gravity: [1, "ngpToastGravity", "gravity"],
          position: [1, "ngpToastPosition", "position"],
          stopOnHover: [1, "ngpToastStopOnHover", "stopOnHover"],
          ariaLive: [1, "ngpToastAriaLive", "ariaLive"]
        },
        exportAs: ["ngpToast"]
      });
    }
  }
  return NgpToast;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NgpToast, NgpToastRef, provideToastConfig };
